import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { courseService } from '../services/courseService';
import { categoryService } from '../services/categoryService';
import { videoProgressService, CourseProgress } from '../services/videoProgressService';
import { enrollmentService } from '../services/enrollmentService';
import { Course, CourseCategory } from '../types';
import { schoolSlideService, SchoolSlide, SchoolTrackKey } from '../services/schoolSlideService';
import { useAuth } from '../hooks/useAuth';
import { summarizeContent } from '../utils/schoolTracks';
import CultivationLevelMap from '../components/CultivationLevelMap';
import { getMediaUrl } from '../utils/media';
import './ClassesPage.css';

const ClassesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | 'all'>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [courseProgressMap, setCourseProgressMap] = useState<{ [courseId: number]: CourseProgress }>({});
  const [trackSlides, setTrackSlides] = useState<Record<SchoolTrackKey, SchoolSlide[]>>({
    short: [],
    long: [],
  });
  const [trackSlideIndexes, setTrackSlideIndexes] = useState<Record<SchoolTrackKey, number>>({
    short: 0,
    long: 0,
  });
  const [showClassExperience, setShowClassExperience] = useState(false);
  const [loadingTrackSlides, setLoadingTrackSlides] = useState(false);
  const [hasEnrollments, setHasEnrollments] = useState<boolean | null>(null);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [showLevelMap, setShowLevelMap] = useState(true);
  const [selectedTrackKey, setSelectedTrackKey] = useState<SchoolTrackKey | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem('selectedSchoolTrack');
    return stored === 'short' || stored === 'long' ? stored : null;
  });
  const { user } = useAuth();

  // Helper to get track label from i18n
  const getTrackLabel = (key: SchoolTrackKey) => t(`schoolTracks.${key}.label`);
  const getTrackDescription = (key: SchoolTrackKey) => t(`schoolTracks.${key}.description`);

  const resolveMediaUrl = useCallback(
    (url?: string | null) => {
      return getMediaUrl(url);
    },
    []
  );

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoryData = await categoryService.list();
        setCategories(categoryData);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const checkEnrollments = async () => {
      if (!user) {
        setLoadingEnrollments(false);
        setHasEnrollments(false);
        return;
      }

      setLoadingEnrollments(true);
      try {
        const enrollments = await enrollmentService.listMyEnrollments();

        if (enrollments.length === 0) {
          try {
            const courseData = await courseService.getCourses(1, 50);
            let hasAnyProgress = false;

            for (const course of courseData.courses.slice(0, 5)) {
              try {
                const progress = await videoProgressService.getCourseProgress(course.id);
                if (progress && (progress.progress > 0 || Object.keys(progress.video_progress || {}).length > 0)) {
                  hasAnyProgress = true;
                  break;
                }
              } catch (error) {
                // Ignore errors, continue checking
              }
            }

            setHasEnrollments(hasAnyProgress);
          } catch (error) {
            console.error('Error checking progress:', error);
            setHasEnrollments(false);
          }
        } else {
          setHasEnrollments(true);
        }
      } catch (error) {
        console.error('Error fetching enrollments:', error);
        setHasEnrollments(false);
      } finally {
        setLoadingEnrollments(false);
      }
    };

    checkEnrollments();
  }, [user]);

  useEffect(() => {
    if (hasEnrollments === true) {
      setShowLevelMap(false);
    } else if (!selectedTrackKey && hasEnrollments === false && !loadingEnrollments) {
      setShowClassExperience(true);
    }
  }, [selectedTrackKey, hasEnrollments, loadingEnrollments]);

  useEffect(() => {
    if (hasEnrollments === false) {
      setShowLevelMap(true);
    } else {
      setShowLevelMap(false);
    }
  }, [hasEnrollments]);

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        const courseData = await courseService.getCourses(
          1,
          50,
          selectedCategoryId === 'all' ? undefined : selectedCategoryId
        );
        setCourses(courseData.courses);

        if (user && courseData.courses.length > 0) {
          const progressPromises = courseData.courses.map(async (course) => {
            try {
              const progress = await videoProgressService.getCourseProgress(course.id);
              return { courseId: course.id, progress };
            } catch (error) {
              return { courseId: course.id, progress: null };
            }
          });

          const progressResults = await Promise.all(progressPromises);
          const progressMap: { [courseId: number]: CourseProgress } = {};
          progressResults.forEach(({ courseId, progress }) => {
            if (progress) {
              progressMap[courseId] = progress;
            }
          });
          setCourseProgressMap(progressMap);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [selectedCategoryId, user]);

  useEffect(() => {
    if (user && courses.length > 0) {
      const refreshEnrollments = async () => {
        try {
          const enrollments = await enrollmentService.listMyEnrollments();
          setHasEnrollments(enrollments.length > 0);
        } catch (error) {
          console.error('Error refreshing enrollments:', error);
        }
      };
      refreshEnrollments();
    }
  }, [courses.length, user]);

  const filteredCourses = courses.filter(course => {
    if (selectedLevel !== 'all' && course.level !== selectedLevel) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    let isMounted = true;
    const fetchTrackSlides = async () => {
      setLoadingTrackSlides(true);
      try {
        const [shortSlides, longSlides] = await Promise.all([
          schoolSlideService.listPublic('short'),
          schoolSlideService.listPublic('long'),
        ]);
        if (!isMounted) return;
        setTrackSlides({
          short: shortSlides,
          long: longSlides,
        });
        setTrackSlideIndexes({
          short: 0,
          long: 0,
        });
      } catch (error) {
        console.error('Error fetching school slides:', error);
      } finally {
        if (isMounted) {
          setLoadingTrackSlides(false);
        }
      }
    };

    fetchTrackSlides();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSlideNavigate = useCallback(
    (trackKey: SchoolTrackKey, direction: 'next' | 'prev') => {
      const totalSlides = trackSlides[trackKey]?.length || 0;
      if (totalSlides <= 1) return;
      setTrackSlideIndexes((prev) => {
        const currentIndex = prev[trackKey] ?? 0;
        const nextIndex =
          direction === 'next'
            ? (currentIndex + 1) % totalSlides
            : (currentIndex - 1 + totalSlides) % totalSlides;
        return { ...prev, [trackKey]: nextIndex };
      });
    },
    [trackSlides]
  );

  const handleConfirmTrack = useCallback(
    async (trackKey: SchoolTrackKey) => {
      try {
        const courseId = await schoolSlideService.getTrackCourse(trackKey);

        if (!courseId) {
          alert(t('classes.alerts.trackNoCourse'));
          return;
        }

        setShowClassExperience(false);
        setSelectedTrackKey(trackKey);
        try {
          window.localStorage.setItem('selectedSchoolTrack', trackKey);
        } catch (error) {
          console.warn('Unable to persist school track selection', error);
        }

        navigate(`/courses/${courseId}`);
      } catch (error) {
        console.error('Error getting track course:', error);
        alert(t('classes.alerts.errorGetCourse'));
      }
    },
    [navigate, t]
  );

  if (hasEnrollments === false && showLevelMap && !loadingEnrollments && !selectedTrackKey) {
    return (
      <div className="classes-page">
        <CultivationLevelMap onClose={() => {
          setShowLevelMap(false);
          setShowClassExperience(true);
        }} />
      </div>
    );
  }

  const trackKeys: SchoolTrackKey[] = ['short', 'long'];
  const accentGradients: Record<SchoolTrackKey, string> = {
    short: 'from-pink-500/90 to-rose-500/90',
    long: 'from-indigo-500/90 to-blue-500/90',
  };

  return (
    <div className="classes-page">
      <div
        className="classes-header classes-header-interactive"
        role="button"
        tabIndex={0}
        onClick={() => {
          setShowClassExperience(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setShowClassExperience(true);
          }
        }}
      >
        <div className="classes-header-content">
          <div>
            <p className="classes-header-eyebrow">{t('classes.eyebrow')}</p>
            <h1>{t('classes.title')}</h1>
            <p className="classes-header-description">
              {loadingTrackSlides
                ? t('classes.loadingSlides')
                : t('classes.slidesDescription')}
            </p>
          </div>
          <div className="classes-header-cta">
            <button
              type="button"
              className="classes-header-button"
              onClick={(event) => {
                event.stopPropagation();
                setShowClassExperience(true);
              }}
            >
              {selectedTrackKey ? t('classes.changeTrack') : t('classes.selectTrackFirst')}
              <span aria-hidden="true">↗</span>
            </button>
            <span className="classes-header-hint">
              {selectedTrackKey
                ? t('classes.currentlySelected', { track: getTrackLabel(selectedTrackKey) })
                : t('classes.clickToOpenSlides')}
            </span>
          </div>
        </div>

        <div className="classes-header-badges">
          {trackKeys.map((trackKey) => {
            const totalSlides = trackSlides[trackKey]?.length || 0;
            const text = loadingTrackSlides
              ? t('classes.loadingText')
              : totalSlides > 0
                ? t('classes.slidesCount', { count: totalSlides })
                : t('classes.waitingAdmin');
            return (
              <span key={trackKey} className="classes-header-badge">
                <span className="badge-label">{getTrackLabel(trackKey)}</span>
                <span className="badge-count">{text}</span>
              </span>
            );
          })}
        </div>
      </div>

      <div className="classes-filters">
        <div className="filter-group">
          <label>{t('classes.filters.category')}</label>
          <select
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="filter-select"
            disabled={!selectedTrackKey}
          >
            <option value="all">{t('classes.filters.all')}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t('classes.filters.level')}</label>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="filter-select"
            disabled={!selectedTrackKey}
          >
            <option value="all">{t('classes.filters.all')}</option>
            <option value="beginner">{t('classes.filters.beginner')}</option>
            <option value="intermediate">{t('classes.filters.intermediate')}</option>
            <option value="advanced">{t('classes.filters.advanced')}</option>
          </select>
        </div>
      </div>

      {hasEnrollments === true || selectedTrackKey ? (
        <div className="classes-grid">
          {filteredCourses.length > 0 ? (
            filteredCourses.map((course) => {
              const isFree = course.price <= 0;
              const CardContainer: any = isFree ? Link : 'div';
              const courseProgress = courseProgressMap[course.id];
              const progress = courseProgress ? Math.round(courseProgress.progress) : 0;
              const thumbnailUrl = resolveMediaUrl(course.thumbnail);

              return (
                <CardContainer
                  key={course.id}
                  {...(isFree ? { to: `/courses/${course.id}` } : {})}
                  className={`class-card-modern ${isFree ? 'class-card-free' : 'class-card-locked'}`}
                >
                  <div
                    className="class-card-thumb"
                    style={{
                      backgroundImage: thumbnailUrl
                        ? `url(${thumbnailUrl})`
                        : 'linear-gradient(135deg,rgb(241, 65, 65) 0%,rgb(89, 240, 215) 100%)',
                    }}
                  >
                    {!isFree && <div className="class-card-lock">🔒</div>}
                    <div className="class-card-thumb-overlay">
                      <div className="class-card-badge">{course.category?.name || t('classes.course.otherCategory')}</div>
                      <h3>{course.title}</h3>
                      <p>{course.description || t('classes.course.updatingDescription')}</p>
                    </div>
                  </div>
                  <div className="class-card-body">
                    <div className="class-card-row">
                      <span className={`class-price-modern ${isFree ? 'price-free' : 'price-paid'}`}>
                        {isFree ? t('classes.course.free') : `${course.price.toLocaleString('vi-VN')} ${t('classes.course.currency')}`}
                      </span>
                      <span className={`class-level-chip level-${course.level}`}>
                        {course.level === 'beginner'
                          ? t('classes.filters.beginner')
                          : course.level === 'intermediate'
                            ? t('classes.filters.intermediate')
                            : t('classes.filters.advanced')}
                      </span>
                    </div>
                    <div className="class-card-stats">
                      <span>📹 {t('classes.course.videoCount', { count: course.video_count || 0 })}</span>
                      <span>⏱ {t('classes.course.progress', { percent: progress })}</span>
                    </div>
                    <div className="class-card-progress">
                      <div className="progress-bar-track">
                        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                      </div>
                      <span>{progress}%</span>
                    </div>
                    {!isFree && (
                      <div className="class-card-locked-notice">
                        {t('classes.course.paidNotice')}
                      </div>
                    )}
                  </div>
                </CardContainer>
              );
            })
          ) : (
            <div className="no-courses">
              <p>{loading ? t('classes.empty.loading') : t('classes.empty.notFound')}</p>
            </div>
          )}
        </div>
      ) : hasEnrollments === false && !loadingEnrollments ? (
        <div className="classes-gate-message">
          <h3>{t('classes.gate.title')}</h3>
          <p>{t('classes.gate.description')}</p>
          <button type="button" onClick={() => setShowClassExperience(true)}>
            {t('classes.gate.button')}
          </button>
        </div>
      ) : loadingEnrollments ? (
        <div className="classes-gate-message">
          <p>{t('classes.checking')}</p>
        </div>
      ) : null}

      {showClassExperience && (
        <div
          className="fixed inset-0 z-[200] bg-gray-900/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-12 px-4"
          onClick={() => {
            setShowClassExperience(false);
            if (hasEnrollments === false) {
              setShowLevelMap(true);
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-label={t('classes.modal.close')}
        >
          <div
            className="bg-white w-full max-w-6xl rounded-[32px] shadow-2xl border border-gray-100 space-y-8 p-6 sm:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-orange-500">{t('classes.modal.eyebrow')}</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2">{t('classes.modal.title')}</h2>
                <p className="text-gray-600 mt-3 max-w-3xl">
                  {t('classes.modal.description')}
                </p>
              </div>
              <button
                type="button"
                className="self-end text-gray-500 hover:text-gray-900 text-3xl leading-none px-3 py-1 rounded-full hover:bg-gray-100 transition"
                onClick={() => {
                  setShowClassExperience(false);
                  if (hasEnrollments === false) {
                    setShowLevelMap(true);
                  }
                }}
                aria-label={t('classes.modal.close')}
              >
                ×
              </button>
            </div>

            <div className="grid gap-8 lg:grid-cols-2 items-stretch">
              {trackKeys.map((trackKey) => {
                const slides = trackSlides[trackKey] || [];
                const slidesCount = slides.length;
                const currentIndex =
                  slidesCount === 0
                    ? 0
                    : Math.min(trackSlideIndexes[trackKey] ?? 0, Math.max(slidesCount - 1, 0));
                const currentSlide = slides[slidesCount > 0 ? currentIndex : 0];

                return (
                  <section
                    key={trackKey}
                    className="rounded-3xl bg-gradient-to-br from-white via-gray-50 to-white border border-gray-100 shadow-inner p-5 flex flex-col gap-4 h-full min-h-[520px]"
                  >
                    <header className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400">{t('classes.modal.trackEyebrow')}</p>
                          <h3 className="text-2xl font-bold text-gray-900">{getTrackLabel(trackKey)}</h3>
                        </div>
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs font-semibold text-white bg-gradient-to-r ${accentGradients[trackKey]}`}
                        >
                          {slidesCount > 0 ? `${currentIndex + 1}/${slidesCount}` : t('classes.modal.updating')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{getTrackDescription(trackKey)}</p>
                    </header>

                    <div className="relative overflow-hidden rounded-2xl bg-white border border-gray-100 shadow-soft flex flex-col h-[400px]">
                      {slidesCount > 0 ? (
                        <>
                          <div className="flex-shrink-0">
                            {currentSlide?.video_url ? (
                              <video
                                src={resolveMediaUrl(currentSlide.video_url)}
                                poster={
                                  currentSlide.thumbnail_url
                                    ? resolveMediaUrl(currentSlide.thumbnail_url)
                                    : undefined
                                }
                                controls
                                className="w-full h-48 object-cover bg-black"
                              />
                            ) : currentSlide?.thumbnail_url ? (
                              <img
                                src={resolveMediaUrl(currentSlide.thumbnail_url)}
                                alt={currentSlide.title}
                                className="w-full h-48 object-cover"
                              />
                            ) : (
                              <div className="h-48 flex items-center justify-center bg-gray-900 text-white text-sm tracking-wide">
                                {t('classes.modal.noMedia')}
                              </div>
                            )}
                          </div>

                          <div className="flex-1 flex flex-col min-h-0 p-5 space-y-3 overflow-hidden">
                            <h4 className="text-lg font-semibold text-gray-900 flex-shrink-0">{currentSlide?.title}</h4>
                            <div
                              className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap break-words flex-1 overflow-y-auto pr-2"
                              style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                            >
                              {currentSlide?.description || t('classes.modal.noDescription')}
                            </div>
                          </div>

                          {slidesCount > 1 && (
                            <>
                              <button
                                type="button"
                                className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/90 text-gray-700 rounded-full w-9 h-9 shadow-md hover:scale-105 transition"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSlideNavigate(trackKey, 'prev');
                                }}
                                aria-label={t('classes.modal.prevSlide')}
                              >
                                ‹
                              </button>
                              <button
                                type="button"
                                className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/90 text-gray-700 rounded-full w-9 h-9 shadow-md hover:scale-105 transition"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSlideNavigate(trackKey, 'next');
                                }}
                                aria-label={t('classes.modal.nextSlide')}
                              >
                                ›
                              </button>
                            </>
                          )}
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center gap-3 text-gray-500 h-full min-h-[320px] p-6">
                          <span className="text-4xl">🛠️</span>
                          <p className="font-semibold text-gray-700">{t('classes.modal.waitingAdminTitle')}</p>
                          <p className="text-sm text-gray-500">
                            {t('classes.modal.waitingAdminDesc', { track: getTrackLabel(trackKey).toLowerCase() })}
                          </p>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      disabled={slidesCount === 0}
                      onClick={() => handleConfirmTrack(trackKey)}
                      className={`w-full mt-2 inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 font-semibold transition ${slidesCount === 0
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                    >
                      {t('classes.modal.agree')}
                      <span aria-hidden="true">→</span>
                    </button>
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassesPage;
